import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export type PlatformAlert = {
  id: string;

  priority: 'P1' | 'P2' | 'P3' | 'P4' | string;
  type: string;
  title: string;
  description: string | null;
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED' | string;
  escalationLevel: number;

  tenantId: string;
  tenantName: string | null;
  locationId: string | null;
  locationName: string | null;
  unitId: string | null;
  unitName: string | null;

  createdAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  nextEscalationAt: string | null;
};

export type PlatformAlertPage = {
  content: PlatformAlert[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

export type PlatformAlertsParams = {
  page: number;
  size: number;
  priority?: string;
  status?: string;
  tenantId?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};

function buildQuery(p: PlatformAlertsParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(p.page));
  sp.set('size', String(p.size));
  if (p.priority) sp.set('priority', p.priority);
  if (p.status) sp.set('status', p.status);
  if (p.tenantId) sp.set('tenantId', p.tenantId);
  sp.set('sortBy', p.sortBy ?? 'createdAt');
  sp.set('sortDir', p.sortDir ?? 'desc');
  return sp.toString();
}

export function usePlatformAlerts(params: PlatformAlertsParams) {
  return useQuery({
    queryKey: ['platform-alerts', params],
    queryFn: () => apiClient<PlatformAlertPage>(`/platform/alerts?${buildQuery(params)}`),
    refetchInterval: 30_000,
  });
}

export function useAcknowledgePlatformAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      apiClient<{ message: string; alertId: string }>(`/platform/alerts/${alertId}/acknowledge`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-alerts'] });
    },
  });
}

export function useResolvePlatformAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      apiClient<{ message: string; alertId: string }>(`/platform/alerts/${alertId}/resolve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-alerts'] });
    },
  });
}
